import { useEffect, useRef } from 'react';

const useBannerSliderPlayback = ({
  sliderRef,
  bannerSliderRef,
  autoplaySpeed,
  shouldResetToFirstSlide,
}) => {
  const activePlayingVideoIdRef = useRef(null);
  const isPausedByVideoRef = useRef(false);
  const isSlideChangingRef = useRef(false);
  const lastSlideCountRef = useRef(0);
  const lastSettingsRef = useRef(null);
  const currentSlideIndexRef = useRef(null);
  const resetSlideTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      clearTimeout(resetSlideTimeoutRef.current);
      activePlayingVideoIdRef.current = null;
      isPausedByVideoRef.current = false;
      isSlideChangingRef.current = false;
      lastSettingsRef.current = null;
      currentSlideIndexRef.current = null;
      resetSlideTimeoutRef.current = null;
    };
  }, []);

  const setLastSlideCount = (slideCount, settings) => {
    lastSlideCountRef.current = slideCount;
    lastSettingsRef.current = settings || null;
  };

  const pauseSliderAutoplay = () => {
    if (sliderRef.current && sliderRef.current.slickPause) {
      sliderRef.current.slickPause();
    }
  };

  const clearResetSlideTimeout = () => {
    clearTimeout(resetSlideTimeoutRef.current);
    resetSlideTimeoutRef.current = null;
  };

  const setSlideResetTimeout = (slideCount, currentSlide) => {
    const settings = lastSettingsRef.current;

    if (
      !slideCount ||
      currentSlide === undefined ||
      currentSlide === null ||
      !shouldResetToFirstSlide(slideCount, settings || {})
    ) {
      return;
    }

    clearResetSlideTimeout();
    resetSlideTimeoutRef.current = setTimeout(() => {
      if (sliderRef.current && sliderRef.current.slickGoTo) {
        sliderRef.current.slickGoTo(0);
      }
    }, autoplaySpeed);
  };

  const resumeSliderAutoplayIfEligible = (
    slideCount,
    { ignoreUserInteraction = false } = {}
  ) => {
    void ignoreUserInteraction;
    const settings = lastSettingsRef.current;

    if (!slideCount) {
      return;
    }

    if (!settings || !settings.autoplay) {
      return;
    }

    if (sliderRef.current && sliderRef.current.slickPlay) {
      sliderRef.current.slickPlay();
    }
  };

  const pauseAllVisibleHtmlVideos = () => {
    if (!bannerSliderRef.current) {
      return;
    }

    const videos = bannerSliderRef.current.querySelectorAll(
      '.Banner__video-container video'
    );

    videos.forEach((video) => {
      if (video && !video.paused) {
        video.pause();
      }
    });
  };

  const onBannerAfterChange = (currentSlide) => {
    isSlideChangingRef.current = false;
    currentSlideIndexRef.current = currentSlide;
    setSlideResetTimeout(
      lastSlideCountRef.current,
      currentSlideIndexRef.current
    );
  };

  const onBannerBeforeChange = () => {
    isSlideChangingRef.current = true;
    clearResetSlideTimeout();

    if (isPausedByVideoRef.current || activePlayingVideoIdRef.current) {
      pauseAllVisibleHtmlVideos();
    }
  };

  const handleVideoPlaybackChange = ({ bannerId, isPlaying }) => {
    if (!bannerId) {
      return;
    }

    if (isPlaying) {
      activePlayingVideoIdRef.current = bannerId;
      isPausedByVideoRef.current = true;
      clearResetSlideTimeout();
      pauseSliderAutoplay();
      return;
    }

    if (activePlayingVideoIdRef.current !== bannerId) {
      return;
    }

    activePlayingVideoIdRef.current = null;

    if (isSlideChangingRef.current) {
      return;
    }

    if (isPausedByVideoRef.current) {
      resumeSliderAutoplayIfEligible(lastSlideCountRef.current, {
        ignoreUserInteraction: true,
      });
    }

    isPausedByVideoRef.current = false;
  };

  const onInteractionStart = () => {
    clearResetSlideTimeout();
  };

  const onInteractionEnd = () => {
    setSlideResetTimeout(
      lastSlideCountRef.current,
      currentSlideIndexRef.current
    );
  };

  return {
    setLastSlideCount,
    onBannerBeforeChange,
    onBannerAfterChange,
    onInteractionStart,
    onInteractionEnd,
    handleVideoPlaybackChange,
  };
};

export default useBannerSliderPlayback;
